import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CheckCircle2 } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_fleet-manager-482/artifacts/fm874382_Image.jpeg";

const empty = {
  name: "",
  phone: "",
  email: "",
  desired_ymm: "",
  engine_type: "",
  transmission: "",
  steering: "",
  colors: "",
  time_frame: "",
  payment: "",
  budget_min: "",
  budget_max: "",
  notes: "",
  hp_website: "", // honeypot
};

export default function Inquire() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [triedSubmit, setTriedSubmit] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onSelect = (k) => (v) => setForm({ ...form, [k]: v === "none" ? "" : v });

  const submit = async (e) => {
    e.preventDefault();
    setTriedSubmit(true);
    // Required dropdowns
    if (!form.engine_type || !form.transmission || !form.steering || !form.time_frame || !form.payment) {
      setError("Please complete all required dropdowns.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...form,
        budget_min: form.budget_min === "" ? null : Number(form.budget_min),
        budget_max: form.budget_max === "" ? null : Number(form.budget_max),
      };
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/inquiries`,
        payload,
      );
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12">
        <div className="max-w-md text-center" data-testid="inquire-success">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
          <h1 className="font-heading text-3xl tracking-tighter font-black text-zinc-950 mt-4">
            Thanks — we got it.
          </h1>
          <p className="text-sm text-zinc-600 mt-2">
            We&apos;ll reach out the moment a matching vehicle is in stock.
          </p>
          <Button
            onClick={() => {
              setDone(false);
              setForm(empty);
            }}
            data-testid="inquire-another-btn"
            className="mt-6 rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold"
          >
            Submit another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-10 sm:py-12">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-6">
          <img src={LOGO_URL} alt="JDMDRE" className="h-10 w-auto object-contain" />
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-zinc-950 text-center">
          Tell us what you&apos;re looking for
        </h1>
        <p className="text-sm text-zinc-600 mt-2 text-center">
          Fill this out and we&apos;ll text or email you when a matching vehicle hits our inventory.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="inquire-form">
          {/* Honeypot — hidden from real users */}
          <div style={{ position: "absolute", left: "-10000px", height: 0, overflow: "hidden" }} aria-hidden="true">
            <label>Don&apos;t fill this out
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.hp_website}
                onChange={onChange("hp_website")}
              />
            </label>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Name *</Label>
            <Input required value={form.name} onChange={onChange("name")} data-testid="inquire-name" className="rounded-sm border-zinc-300" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Phone *</Label>
              <Input required type="tel" value={form.phone} onChange={onChange("phone")} data-testid="inquire-phone" className="rounded-sm border-zinc-300 font-mono" placeholder="(555) 123-4567" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Email *</Label>
              <Input required type="email" value={form.email} onChange={onChange("email")} data-testid="inquire-email" className="rounded-sm border-zinc-300" placeholder="you@example.com" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Year Make Model *</Label>
            <Input required value={form.desired_ymm} onChange={onChange("desired_ymm")} data-testid="inquire-ymm" placeholder="Ex 1993 Toyota Supra" className="rounded-sm border-zinc-300" />
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Engine *</Label>
            <Select value={form.engine_type || "none"} onValueChange={onSelect("engine_type")}>
              <SelectTrigger className={`rounded-sm border-zinc-300 ${!form.engine_type && triedSubmit ? "border-red-500" : ""}`} data-testid="inquire-engine"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="na">NA / Non Turbo</SelectItem>
                <SelectItem value="turbo">Turbo / Twin Turbo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Transmission *</Label>
              <Select value={form.transmission || "none"} onValueChange={onSelect("transmission")}>
                <SelectTrigger className={`rounded-sm border-zinc-300 ${!form.transmission && triedSubmit ? "border-red-500" : ""}`} data-testid="inquire-transmission"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="automatic">Automatic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Steering *</Label>
              <Select value={form.steering || "none"} onValueChange={onSelect("steering")}>
                <SelectTrigger className={`rounded-sm border-zinc-300 ${!form.steering && triedSubmit ? "border-red-500" : ""}`} data-testid="inquire-steering"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lhd">Left Hand Drive</SelectItem>
                  <SelectItem value="rhd">Right Hand Drive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Colors *</Label>
            <Input required value={form.colors} onChange={onChange("colors")} data-testid="inquire-colors" placeholder="e.g. White, Black" className="rounded-sm border-zinc-300" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Time Frame *</Label>
              <Select value={form.time_frame || "none"} onValueChange={onSelect("time_frame")}>
                <SelectTrigger className={`rounded-sm border-zinc-300 ${!form.time_frame && triedSubmit ? "border-red-500" : ""}`} data-testid="inquire-timeframe"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="now">Ready to Buy Now</SelectItem>
                  <SelectItem value="3_6">3 to 6 months</SelectItem>
                  <SelectItem value="6_12">6 to 12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Payment *</Label>
              <Select value={form.payment || "none"} onValueChange={onSelect("payment")}>
                <SelectTrigger className={`rounded-sm border-zinc-300 ${!form.payment && triedSubmit ? "border-red-500" : ""}`} data-testid="inquire-payment"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="financing">Financing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Budget Min ($) *</Label>
              <Input required type="number" step="0.01" value={form.budget_min} onChange={onChange("budget_min")} data-testid="inquire-budget-min" className="rounded-sm border-zinc-300 font-mono" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Budget Max ($) *</Label>
              <Input required type="number" step="0.01" value={form.budget_max} onChange={onChange("budget_max")} data-testid="inquire-budget-max" className="rounded-sm border-zinc-300 font-mono" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Anything else?</Label>
            <Textarea value={form.notes} onChange={onChange("notes")} data-testid="inquire-notes" rows={3} placeholder="Trim, mileage, must-haves, etc." className="rounded-sm border-zinc-300" />
          </div>

          {error && (
            <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm" data-testid="inquire-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="inquire-submit-btn"
            className="w-full rounded-sm bg-[#002FA7] hover:bg-[#002075] text-white font-semibold py-6"
          >
            {submitting ? "Sending..." : "Submit"}
          </Button>

          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400 text-center">
            We&apos;ll only contact you about vehicle availability.
          </p>
        </form>
      </div>
    </div>
  );
}
